/* eslint-disable class-methods-use-this */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  CreateOrUpdateRentalListingRequest,
  LinkName,
  PmBuildingType,
  RentalListingClient,
  RentalListingDocument,
  RentalListingDto,
} from '@core/api';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { imageDocumentValidator } from '../../shared/validators';
import { WebsiteEditCoordinatesService } from '../../website-edit-coordinates.service';
import { ConfirmationDialogComponent } from '../rental-listing-edit-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-rental-listing-edit',
  templateUrl: './rental-listing-edit.component.html',
  styleUrls: ['./rental-listing-edit.component.scss'],
  standalone: false,
})
export class RentalListingEditComponent implements OnInit {
  form = new FormGroup({});
  formFields: FormlyFieldConfig[];
  formModel: any = {};
  isLoading = false;
  maxFileSizeInMB = 10;
  rentalListingId: number | null = null;

  constructor(
    private translateService: TranslateService,
    private fieldService: LocationFormlyFieldService,
    private rentalListingClient: RentalListingClient,
    private toastr: ToastrService,
    private coordinatesService: WebsiteEditCoordinatesService,
    private dialogRef: MatDialogRef<RentalListingEditComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      rentalListingObject: RentalListingDto;
      buildingTypes: PmBuildingType[];
    },
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.data.rentalListingObject) {
      this.rentalListingId = this.data.rentalListingObject.id;

      const buildingTypeIds = this.data.rentalListingObject.buildingTypes.map(
        type => type.id
      );
      this.formModel = {
        searchResult: {
          id: this.data.rentalListingObject.archObjId,
          streetName: this.data.rentalListingObject.address?.streetName
            ? this.data.rentalListingObject.address.streetName
            : '',
          postalCode: this.data.rentalListingObject.address.postalCode,
          city: this.data.rentalListingObject.address?.city,
        },
        title: this.data.rentalListingObject.title,
        specificPrice: this.data.rentalListingObject.specificPrice,
        buildingTypes: Array.isArray(
          this.data.rentalListingObject.buildingTypes
        )
          ? buildingTypeIds
          : this.data.rentalListingObject.buildingTypes
            ? [buildingTypeIds]
            : [],
        areaSpecification: this.data.rentalListingObject.areaSpecification,
        description: this.data.rentalListingObject.description,
        additionalInformation:
          this.data.rentalListingObject.additionalInformation,
        link: this.data?.rentalListingObject?.links[0]?.link ?? '',
        linkDisplayName:
          this.data?.rentalListingObject?.links[0]?.linkDisplayName ?? '',
        linkId: this.data?.rentalListingObject?.links[0]?.id ?? null,
        links:
          this.data?.rentalListingObject?.links?.slice(1)?.map(link => ({
            id: link.id,
            link: link.link,
            linkDisplayName: link.linkDisplayName,
          })) ?? [],
        isPublished: this.data.rentalListingObject.isPublished,
        documents: this.data.rentalListingObject.documents.map(doc => ({
          file: {
            id: doc.id,
            name: doc.fileName,
            size: 0,
            type: 'image/jpeg',
            url: doc.fileUri,
          },
          isPrimary: doc.id === this.data.rentalListingObject.primaryDocumentId,
        })),
      };
      this.form.patchValue(this.formModel);
    }
    this.initiateForm();
  }

  /**
   * Deletes the rental listing.
   */
  deleteRentalListing(): void {
    if (!this.rentalListingId) return;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.rentalListingClient
          .deleteRentalListing(this.rentalListingId)
          .subscribe({
            next: () => {
              const successMessage = this.translateService.instant(
                'website_edit.successDelete'
              );
              this.isLoading = false;
              this.toastr.success(successMessage);
              this.dialogRef.close({ deleted: true });
            },
            error: error => {
              console.error('Fehler beim Löschen der Anfrage:', error);
              const errorMessage = this.translateService.instant(
                'website_edit.errorDelete'
              );
              const errorTitle = this.translateService.instant('generic.error');
              this.isLoading = false;
              this.toastr.error(errorMessage, errorTitle);
            },
          });
      }
    });
  }

  closeDialog(): void {
    this.dialogRef.close({ cancelled: true });
  }

  async submitForm() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    try {
      this.isLoading = true;
      const request = await this.createRentalListingRequest(this.form.value);

      if (this.rentalListingId) {
        this.rentalListingClient
          .updateRentalListing(this.rentalListingId, request)
          .subscribe({
            next: () => {
              const successMessage = this.translateService.instant(
                'website_edit.successUpdate'
              );
              this.isLoading = false;
              this.toastr.success(successMessage);
              this.dialogRef.close({ cancelled: false });
            },
            error: error => {
              console.error('Fehler beim Aktualisieren der Anfrage:', error);
              const errorMessage = this.translateService.instant(
                'website_edit.errorUpdate'
              );
              const errorTitle = this.translateService.instant('generic.error');
              this.isLoading = false;
              this.toastr.error(errorMessage, errorTitle);
            },
          });
      } else {
        this.isLoading = true;
        this.rentalListingClient.createRentalListing(request).subscribe({
          next: () => {
            const successMessage = this.translateService.instant(
              'website_edit.successCreate'
            );
            this.isLoading = false;
            this.toastr.success(successMessage);
            this.dialogRef.close({ cancelled: false });
          },
          error: error => {
            console.error('Fehler beim Erstellen der Anfrage:', error);
            const errorMessage = this.translateService.instant(
              'website_edit.errorCreate'
            );
            const errorTitle = this.translateService.instant('generic.error');
            this.isLoading = false;
            this.toastr.error(errorMessage, errorTitle);
          },
        });
      }
    } catch (error) {
      console.error('Fehler beim Erstellen der Anfrage:', error);
      const errorMessage = this.translateService.instant(
        'generic.errorMessage'
      );
      const errorTitle = this.translateService.instant('generic.error');
      this.isLoading = false;
      this.toastr.error(errorMessage, errorTitle);
    }
  }

  /**
   * Creates a rental listing request from the form model.
   * @param formModel The form model.
   * @returns The rental listing request.
   */
  private async createRentalListingRequest(
    formModel: any
  ): Promise<CreateOrUpdateRentalListingRequest> {
    const images = await this.processImages(formModel.documents);
    const address = this.formatAddress(formModel.searchResult);
    const coordinates =
      await this.coordinatesService.getCoordinatesFromArchObjAddress(address);
    const links: LinkName[] = [];

    if (formModel.link && formModel.linkDisplayName) {
      links.push({
        id: formModel.linkId,
        link: formModel.link,
        linkDisplayName: formModel.linkDisplayName,
      });
    }

    if (formModel.links && formModel.links.length > 0) {
      formModel.links.forEach((element: LinkName) => {
        if (element.link && element.linkDisplayName) {
          links.push(element);
        }
      });
    }

    const filteredBuildingTypes = this.data.buildingTypes
      .filter(type => this.formModel.buildingTypes?.includes(type.id))
      .map(type => ({
        id: type.id,
        name: type.buildingType,
      }));
    return {
      archObjId: formModel.searchResult.id || '',
      title: formModel.title || '',
      description: formModel.description || '',
      links,
      isPublished: formModel.isPublished || false,
      latitude: coordinates[0] || undefined,
      longitude: coordinates[1] || undefined,
      documents: images,
      additionalInformation: formModel.additionalInformation,
      areaSpecification: formModel.areaSpecification,
      buildingTypes: filteredBuildingTypes,
      specificPrice: formModel.specificPrice,
    };
  }

  /**
   * Processes the images for the rental listing.
   * @param images The images to process.
   * @returns The processed images.
   */
  private async processImages(images: any[]): Promise<RentalListingDocument[]> {
    if (!images) return [];

    const processedImages = await Promise.all(
      images.map(async doc => {
        if (doc.file && !doc.file.id) {
          const base64Content = await this.readDocument(doc);
          return {
            id: doc.file?.id || null,
            content: base64Content,
            fileName: doc.file.name,
            isPrimary: doc.isPrimary || false,
            isImage: true,
          } as RentalListingDocument;
        } else {
          return {
            id: doc.file.id,
            content: null,
            fileName: null,
            isPrimary: doc.isPrimary || false,
            isImage: true,
          } as RentalListingDocument;
        }
      })
    );

    return processedImages.filter(
      doc => doc !== null
    ) as RentalListingDocument[];
  }

  /**
   * Reads the document and returns its base64 content.
   * @param doc The document to read.
   * @returns The base64 content of the document.
   */
  private readDocument(doc: any): Promise<string | null> {
    return new Promise<string | null>((resolve, reject) => {
      if (!doc || !doc.file) {
        resolve(null);
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const base64Content = (reader.result as string).split(',')[1];
        resolve(base64Content);
      };

      reader.onerror = error => {
        console.error('Fehler beim Lesen der Datei:', error);
        reject(error);
      };

      reader.readAsDataURL(doc.file);
    });
  }

  /**
   * Formats the address from the search result.
   * @param searchResult The search result containing the address.
   * @returns The formatted address.
   */
  private formatAddress(searchResult: any): string {
    if (!searchResult) return '';

    const { streetName, houseNumber, postalCode, city } = searchResult;

    const addressParts = [
      streetName,
      houseNumber ? ` ${houseNumber}` : '',
      (streetName || houseNumber) && (postalCode || city) ? ', ' : '',
      postalCode,
      postalCode && city ? ' ' : '',
      city,
    ];

    return addressParts.join('').trim().replace(/,\s*$/, '');
  }

  initiateForm(): void {
    this.formFields = [
      {
        type: 'chapter',
        props: {
          chapterTitle: this.translateService.instant(
            'location.myLocation.label__search_location'
          ),
        },
      },
      ...this.fieldService.autocompleteSearch(
        false,
        null,
        true,
        null,
        null,
        null,
        this.translateService.instant(
          'location.shared.delete_location_search_tooltip'
        )
      ),
      {
        type: 'chapter',
        props: {
          chapterTitle: this.translateService.instant(
            'website_edit.dialog.label__adInformation'
          ),
        },
      },
      {
        key: 'title',
        type: 'input',
        props: {
          label: this.translateService.instant(
            'website_edit.table_header.label__title'
          ),
          required: true,
          maxLength: 100,
        },
      },
      {
        key: 'specificPrice',
        type: 'input',
        props: {
          label: this.translateService.instant(
            'website_edit.table_header.label__price'
          ),
          maxLength: 50,
        },
      },
      {
        key: 'buildingTypes',
        type: 'select',
        props: {
          label: this.translateService.instant(
            'website_edit.table_header.label__buildingType'
          ),
          multiple: true,
          options: this.data.buildingTypes.map(type => ({
            value: type.id,
            label: type.buildingType,
          })),
        },
        expressions: {
          'props.options': (field: any) => {
            const currentValue = field.model?.buildingTypes || [];
            return this.data.buildingTypes.map(type => ({
              value: type.id,
              label: type.buildingType,
              disabled:
                Array.isArray(currentValue) &&
                currentValue.length >= 3 &&
                !currentValue.includes(type.id),
            }));
          },
        },
      },
      {
        key: 'areaSpecification',
        type: 'input',
        props: {
          label: this.translateService.instant(
            'website_edit.table_header.label__area'
          ),
          maxLength: 50,
        },
      },
      {
        key: 'description',
        type: 'textarea',
        props: {
          label: this.translateService.instant(
            'website_edit.table_header.label__description'
          ),
          maxLength: 1000,
          rows: 5,
        },
      },
      {
        key: 'additionalInformation',
        type: 'textarea',
        defaultValue: this.translateService.instant(
          'website_edit.rental.further_information_default'
        ),
        props: {
          label: this.translateService.instant(
            'website_edit.dialog.label__moreInformation'
          ),
          maxLength: 500,
          rows: 2,
        },
      },
      {
        key: 'linkId',
        template: '',
      },
      {
        key: 'link',
        type: 'input',
        props: {
          label: this.translateService.instant(
            'website_edit.table_header.label__link'
          ),
          maxLength: 100,
          type: 'url',
          pattern: '^(https?://).*$',
        },
        validation: {
          messages: {
            pattern: () =>
              this.translateService.instant('website_edit.dialog.error__link'),
          },
        },
      },
      {
        key: 'linkDisplayName',
        type: 'input',
        props: {
          label: this.translateService.instant(
            'website_edit.table_header.label__linkDisplayName'
          ),
          maxLength: 100,
        },
      },
      {
        key: 'links',
        type: 'repeat-multiple-fields',
        props: {
          addText: this.translateService.instant(
            'website_edit.dialog.label__addLink'
          ),
          tooltipForRemove: this.translateService.instant(
            'website_edit.dialog.tooltip__removeLink'
          ),
        },
        fieldArray: {
          type: 'flex-layout',
          fieldGroup: [
            {
              key: 'id',
              template: '',
            },
            {
              key: 'link',
              type: 'input',
              props: {
                label: this.translateService.instant(
                  'website_edit.table_header.label__link'
                ),
                maxLength: 100,
                type: 'text',
                pattern: '^(https?://).*$',
              },
              validation: {
                messages: {
                  pattern: () =>
                    this.translateService.instant(
                      'website_edit.dialog.error__link'
                    ),
                },
              },
            },
            {
              key: 'linkDisplayName',
              type: 'input',
              props: {
                label: this.translateService.instant(
                  'website_edit.table_header.label__linkDisplayName'
                ),
                maxLength: 100,
              },
            },
          ],
        },
      },
      {
        type: 'chapter',
        props: {
          chapterTitle: this.translateService.instant(
            'website_edit.table_header.label__images'
          ),
        },
      },
      {
        key: 'documents',
        type: 'repeat',
        props: {
          addText: this.translateService.instant(
            'ticket.create.button__attach_new_image'
          ),
        },
        validators: {
          validation: [imageDocumentValidator],
        },
        validation: {
          messages: {
            missingFile: () =>
              this.translateService.instant(
                'website_edit.dialog.error__missingFile'
              ),
            noPrimary: () =>
              this.translateService.instant(
                'website_edit.dialog.error__noPrimaryImage'
              ),
            multiplePrimary: () =>
              this.translateService.instant(
                'website_edit.dialog.error__multiplePrimaryImages'
              ),
          },
        },
        fieldArray: {
          type: 'flex-layout',
          fieldGroup: [
            {
              type: 'file-pictures',
              key: 'file',
              props: {
                label: this.translateService.instant(
                  'ticket.create.label__attachment'
                ),
                visibleInConfirm: true,
                accept: '.jpg, jpeg, .png, .webp, .tif, .tiff',
                maxFileSizeInMB: this.maxFileSizeInMB,
              },
            },
            {
              key: 'isPrimary',
              wrappers: ['no-border'],
              type: 'checkbox',
              defaultValue: false,
              props: {
                label: this.translateService.instant(
                  'website_edit.table_header.label__mainDisplayImage'
                ),
                required: true,
              },
            },
          ],
        },
      },
      {
        type: 'chapter',
      },
      {
        type: 'checkbox',
        wrappers: ['no-border'],
        key: 'isPublished',
        defaultValue: false,
        props: {
          label: this.translateService.instant(
            'website_edit.table_header.label__publish'
          ),
          margin: '-35px 0 0 0',
        },
      },
    ];
  }
}
